import { Item as DropdownMenuItem } from '@radix-ui/react-dropdown-menu';
import { useMemo, useState } from 'react';

import { ButtonDropdown } from '@/components/core/ButtonDropdown/ButtonDropdown';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/core/DropdownMenu/DropdownMenu';
import { useTranslation } from '@/hooks/useTranslation';

import { FilterAttributeOption } from './FilterBuilder.types';
import { getAttributeName } from './filterGroups';

interface AttributePickerProps {
    attributes: FilterAttributeOption[];
    value?: FilterAttributeOption | string | null;
    onChange: (attribute: FilterAttributeOption | null) => void;
}

function attributeLabel(attribute: FilterAttributeOption): string {
    return attribute.translatedLabel || attribute.label || attribute.name;
}

const AttributePicker = ({ attributes, value, onChange }: AttributePickerProps) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const selectedName = getAttributeName(value);
    const selected = useMemo(
        () => attributes.find((attribute) => attribute.name === selectedName) ?? (selectedName ? { name: selectedName } : null),
        [attributes, selectedName],
    );

    return (
        <div className="tw-min-w-[8rem] tw-flex-1 tw-overflow-hidden">
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                <DropdownMenuTrigger asChild={true}>
                    <ButtonDropdown
                        className="tw-w-full tw-min-w-0 tw-max-w-full
                            [&>button]:tw-w-full [&>button]:tw-min-w-0 [&>button]:tw-max-w-full [&>button]:tw-overflow-hidden
                            [&>button>div]:tw-min-w-0 [&>button>div]:tw-flex-1 [&>button>div]:tw-overflow-hidden [&>button>div]:tw-max-w-full
                            [&>button>div>div]:tw-min-w-0 [&>button>div>div]:tw-max-w-full [&>button>div>div]:tw-overflow-hidden"
                        titleOne={selected ? attributeLabel(selected) : t('CHOOSE_ATTRIBUTE')}
                        isOpen={isOpen}
                        aria-label={selected ? attributeLabel(selected) : t('CHOOSE_ATTRIBUTE')}
                    />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="start"
                    hasBorder={true}
                    hasPadding={false}
                    className="tw-max-h-64 tw-overflow-y-auto tw-min-w-[12rem]"
                >
                    {attributes.map((attribute) => (
                        <DropdownMenuItem
                            key={attribute.name}
                            className="tw-flex tw-flex-col tw-items-start tw-px-3 tw-py-2 tw-text-t-sm tw-cursor-pointer
                            tw-outline-none data-[highlighted]:tw-bg-fill-primary-hover hover:tw-bg-fill-primary-hover"
                            onSelect={() => onChange(attribute)}
                        >
                            <span className="tw-font-bold tw-text-t-primary">{attributeLabel(attribute)}</span>
                            <span className="tw-text-t-xs tw-text-t-secondary">{attribute.name}</span>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};

AttributePicker.displayName = 'AttributePicker';

export { AttributePicker };
